'use client';

import { DynamicForm } from '@/components/forms/DynamicForm/DynamicForm';
import { submitDynamicForm } from '@/lib/client/forms/submitDynamicForm';
import { isMarketingConsentEnabled } from '@/lib/forms/marketingConsent';
import { pushFormSuccessGtmEvent } from '@/lib/analytics/pushFormSuccessGtmEvent';
import { sanitizeClientFormMetadata } from '@/lib/forms/sanitizeClientFormMetadata';

import type { FieldConfig } from '@/types/forms';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';
import type { SecurityConfig } from './types';

type SmartFormInlineProps<T extends FieldValues> = {
  formId: string;
  schema: ZodType<T>;
  fields: FieldConfig[];
  defaultValues: DefaultValues<T>;
  submitLabel?: string;
  columns?: 1 | 2;
  security?: SecurityConfig;
  metadata?: Record<string, unknown>;
  buildMetadata?: () => Record<string, unknown>;
  recipientContext?: Record<string, unknown>;
  renderFooterExtra?: (values: T) => React.ReactNode;
  buttonContainerClassName?: string;
  buttonClassName?: string;
  buttonPaddingClassName?: string;
  disableSubmitUntilFilled?: boolean;
  className?: string;
};

export function SmartFormInline<T extends FieldValues>({
  formId,
  schema,
  fields,
  defaultValues,
  submitLabel = 'Submit',
  columns = 2,
  security = { honeypot: true, honeypotName: 'lips-name' },
  metadata,
  buildMetadata,
  recipientContext,
  renderFooterExtra,
  buttonContainerClassName,
  buttonClassName,
  buttonPaddingClassName,
  disableSubmitUntilFilled,
  className,
}: SmartFormInlineProps<T>) {
  return (
    <DynamicForm<T>
      mode="inline"
      schema={schema}
      fields={fields}
      defaultValues={defaultValues}
      columns={columns}
      submitLabel={submitLabel}
      className={className}
      security={security}
      showMarketingConsent={isMarketingConsentEnabled(formId)}
      renderFooterExtra={renderFooterExtra}
      buttonContainerClassName={buttonContainerClassName}
      buttonClassName={buttonClassName}
      buttonPaddingClassName={buttonPaddingClassName}
      disableSubmitUntilFilled={disableSubmitUntilFilled}
      onSubmit={async (values, context) => {
        await submitDynamicForm({
          formId,
          values,
          metadata: sanitizeClientFormMetadata({
            pageUrl:
              typeof window !== 'undefined' ? window.location.href : undefined,
            ...metadata,
            ...buildMetadata?.(),
          }),
          security: {
            honeypotName: context?.security?.honeypotName,
            honeypot: context?.security?.honeypot,
          },
          ...(recipientContext ? { recipientContext } : {}),
        });

        pushFormSuccessGtmEvent(formId);
      }}
    />
  );
}
